import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { concatLatestFrom } from '@ngrx/operators';
import { Store } from '@ngrx/store';
import { map as _map } from 'lodash';
import {
  EMPTY,
  catchError,
  combineLatest,
  map,
  mergeMap,
  of,
  startWith,
  takeWhile
} from 'rxjs';
import { AuthActions } from '@components/auth/auth.actions';
import { QuestionnaireQuickAccessConfig } from '@components/questionnaire/quick-access-modal/quick-access-modal.config';
import { selectReportTypeIsMiniReportHasQustionnaireForm } from '@pages/report/report.selectors';
import { ReportQuestionnaireAPIService } from '@providers/api/report-questionnaire/report-questionnaire.service';
import { Logger } from '../../../utils/logger';
import { QuestionnaireQuickAccessModalActions } from './quick-access-modal.actions';

/**
 * @description Questionnaire Quick Access Modal Effects.
 * @exports
 * @class QuestionnaireQuickAccessModalEffects
 */
@Injectable()
export class QuestionnaireQuickAccessModalEffects extends ReportQuestionnaireAPIService {
  private readonly actions = inject(Actions);
  private readonly store = inject(Store);

  private readonly logger = Logger.getInstance(
    QuestionnaireQuickAccessModalEffects.name
  );

  readonly onAuthLogout_SwitchProfile = createEffect(
    () => {
      return this.actions.pipe(
        ofType(AuthActions.logout, AuthActions.switchProfile),
        map(() => QuestionnaireQuickAccessModalActions.reset())
      );
    },
    { dispatch: true }
  );

  readonly onCheckIfCanFillOutTheQuestionnaire = createEffect(
    () => {
      const logger = this.logger.child(`onCheckIfCanFillOutTheQuestionnaire`);

      return this.actions.pipe(
        ofType(
          QuestionnaireQuickAccessModalActions.checkIfCanFillOutTheQuestionnaire
        ),
        takeWhile(() => this.session.isAuthenticated()),
        takeWhile(() => QuestionnaireQuickAccessConfig.ACTIVE),
        concatLatestFrom(() => [
          this.store.select(selectReportTypeIsMiniReportHasQustionnaireForm)
        ]),
        mergeMap(
          ([
            { reportType, showModal = true, showModalIsBeingProcessed = false },
            miniReports
          ]) => {
            const miniReport = _map(
              miniReports,
              ({ code }: { code: string }) => code
            ) as unknown as Array<string>;

            const email = this.session.getProfile()?.email;

            if (!email) return EMPTY;

            if (reportType) {
              if (!miniReport.includes(reportType)) {
                return of(
                  QuestionnaireQuickAccessModalActions.updateStatus({
                    status: null
                  }),
                  QuestionnaireQuickAccessModalActions.checkIfCanFillOutTheQuestionnaireSuccess(
                    {
                      reportType,
                      status: null
                    }
                  ),
                  QuestionnaireQuickAccessModalActions.updateLoading({
                    questionnaireCheck: false
                  })
                );
              }

              // Handle specific reportType
              return this.checkUserQuestionnaireReport({
                data: { email, type: reportType }
              }).pipe(
                mergeMap(({ data }) => {
                  const action: Array<any> = [];

                  if (showModal) {
                    if (data === 'PENDING') {
                      action.push(
                        QuestionnaireQuickAccessModalActions.updateAccessQuestionnaire(
                          { accessQuestionnaire: true }
                        )
                      );
                    } else if (
                      data === 'COMPLETED' &&
                      showModalIsBeingProcessed
                    ) {
                      action.push(
                        QuestionnaireQuickAccessModalActions.updateIsBeingProcessed(
                          { isBeingProcessed: true }
                        )
                      );
                    }
                  }

                  if (action.length > 0) {
                    return of(
                      QuestionnaireQuickAccessModalActions.updateReportType({
                        reportType: reportType
                      }),
                      QuestionnaireQuickAccessModalActions.updateStatus({
                        status: data as unknown as string
                      }),
                      QuestionnaireQuickAccessModalActions.updateLoading({
                        questionnaireCheck: false
                      }),
                      ...action
                    );
                  }
                  return of(
                    QuestionnaireQuickAccessModalActions.updateReportType({
                      reportType: reportType
                    }),
                    QuestionnaireQuickAccessModalActions.updateStatus({
                      status: data ?? null
                    }),
                    QuestionnaireQuickAccessModalActions.checkIfCanFillOutTheQuestionnaireSuccess(
                      {
                        reportType,
                        status: data ?? null
                      }
                    ),
                    QuestionnaireQuickAccessModalActions.updateLoading({
                      questionnaireCheck: false
                    })
                  );
                }),
                catchError((error) => {
                  logger.error(
                    'Error in checkUserQuestionnaireReport with reportType',
                    error
                  );
                  return of(
                    // QuestionnaireQuickAccessModalActions.updateReportType({
                    //   reportType: null
                    // }),
                    QuestionnaireQuickAccessModalActions.updateStatus({
                      status: 'NOT_FOUND'
                    }),
                    QuestionnaireQuickAccessModalActions.checkIfCanFillOutTheQuestionnaireFailed(
                      {
                        reportType,
                        error: {
                          message: error.message
                        }
                      }
                    ),
                    QuestionnaireQuickAccessModalActions.updateLoading({
                      questionnaireCheck: false
                    })
                  );
                }),
                startWith(
                  QuestionnaireQuickAccessModalActions.updateAccessQuestionnaire(
                    { accessQuestionnaire: false }
                  ),
                  QuestionnaireQuickAccessModalActions.updateReportType({
                    reportType: null
                  }),
                  QuestionnaireQuickAccessModalActions.updateStatus({
                    status: null
                  }),
                  QuestionnaireQuickAccessModalActions.updateLoading({
                    questionnaireCheck: true
                  })
                )
              );
            }

            // Handle when reportType is not provided
            return combineLatest(
              _map(miniReport, (reportType) =>
                this.checkUserQuestionnaireReport({
                  data: { email, type: reportType }
                }).pipe(
                  catchError((error) => {
                    logger.error(
                      `Error in combined report checks [${reportType}]`,
                      error
                    );
                    return of({ data: null, type: reportType });
                  })
                )
              )
            ).pipe(
              mergeMap((response) => {
                const action: Array<any> = [];
                const pending = response.findIndex(
                  (o: any) => o.data === 'PENDING'
                );

                if (pending > -1) {
                  if (showModal) {
                    action.push(
                      QuestionnaireQuickAccessModalActions.updateAccessQuestionnaire(
                        {
                          accessQuestionnaire: true
                        }
                      )
                    );
                  }
                  action.push(
                    QuestionnaireQuickAccessModalActions.updateReportType({
                      reportType: miniReport[pending]
                    })
                  );
                }

                if (action.length > 0) {
                  return of(...action);
                }

                return EMPTY;
              }),
              startWith(
                QuestionnaireQuickAccessModalActions.updateAccessQuestionnaire({
                  accessQuestionnaire: false
                }),
                QuestionnaireQuickAccessModalActions.updateReportType({
                  reportType: null
                }),
                QuestionnaireQuickAccessModalActions.updateStatus({
                  status: null
                }),
                QuestionnaireQuickAccessModalActions.updateLoading({
                  questionnaireCheck: true
                })
              )
            );
          }
        )
      );
    },
    { dispatch: true }
  );
}
